/**
 * TraderPersona.jsx — v2 port of v1 renderTraderPersonaContent (#238).
 *
 * Shows the logged-in user's weekly trading snapshot, guild ranks, recent
 * sales, and Fissal's quick take. Includes an editable persona note that
 * is stored as `traderPersonaNote` in /api/v2/me/prefs.
 *
 * Voice tone: Jone (bright, fast) for the advice placeholder; no deep
 * archive monologues here — link to FissalAI for those.
 */
import { createSignal, For, Show, onMount } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import api from '../lib/apiClient.js';
import { gold } from '../lib/format.js';
import toast from '../lib/toastStore.js';
import { EMOJI } from '../lib/emoji.js';

export default function TraderPersona() {
  const { data, loading, error, refetch } = useWidgetData('trader-persona', () => ({}));

  // Persona note — stored in prefs, loaded once on mount.
  const [note,      setNote]      = createSignal('');
  const [editNote,  setEditNote]  = createSignal(false);
  const [savingNote, setSavingNote] = createSignal(false);

  onMount(async () => {
    try {
      const r = await api.getPrefs();
      setNote(r?.prefs?.traderPersonaNote || '');
    } catch { /* prefs optional */ }
  });

  async function saveNote() {
    setSavingNote(true);
    try {
      const r   = await api.getPrefs();
      const cur = r?.prefs || {};
      await api.putPrefs({ ...cur, traderPersonaNote: note() });
      setEditNote(false);
    } catch (err) {
      toast.error('Save failed', { detail: err?.message });
    } finally {
      setSavingNote(false);
    }
  }

  const weekly      = () => data()        || {};
  const recentSales = () => weekly().recentSales || [];
  const guildRanks  = () => weekly().guildRanks  || [];
  const weeklyGold  = () => weekly().weeklyGold  || 0;
  const weeklySales = () => weekly().weeklySales || 0;

  return (
    <div class="v2-widget">
      <div class="v2-widget-hdr">
        <span class="v2-widget-hdr__sub">{EMOJI.post} Trader persona</span>
        <button class="v2-widget-hdr__btn" style={{ 'margin-left': 'auto' }}
          title="Refresh" onClick={refetch}>↻</button>
      </div>

      <Show when={!loading()} fallback={
        <div class="v2-widget-skel">Jone pings… loading your frequencies.</div>
      }>
        <Show when={!error()} fallback={<div class="v2-error">{error()}</div>}>

          <Show when={weekly().loggedIn} fallback={
            <div class="v2-dim" style={{ padding: '10px' }}>
              Log in to view your trader persona.
            </div>
          }>
            <div class="v2-widget__scroll">

              {/* ── Weekly stats ── */}
              <div class="v2-section-h">This week</div>
              <div class="v2-stat-grid">
                <div class="v2-stat v2-stat--gold">
                  <span class="v2-stat__label">Gold earned</span>
                  <span class="v2-stat__value">{gold(weeklyGold())}</span>
                </div>
                <div class="v2-stat v2-stat--cyan">
                  <span class="v2-stat__label">Sales</span>
                  <span class="v2-stat__value">{weeklySales().toLocaleString()}</span>
                </div>
              </div>

              {/* ── Guild ranks ── */}
              <Show when={guildRanks().length > 0}>
                <div class="v2-section-h">Guild ranks</div>
                <For each={guildRanks()}>{(r) => (
                  <div class="v2-tp__rank-row">
                    <span class="v2-tp__rank-guild">{r.guildName}</span>
                    <span class="v2-dim" style={{ 'font-size': '11px' }}>
                      {r.rank || 'Member'}
                    </span>
                  </div>
                )}</For>
              </Show>

              {/* ── Recent sales ── */}
              <div class="v2-section-h">Recent sales</div>
              <Show when={recentSales().length > 0} fallback={
                <div class="v2-dim" style={{ 'font-size': '11px', padding: '4px 0' }}>
                  No sales this week. Jode awaits your next offering.
                </div>
              }>
                <div class="v2-tp__sales">
                  <For each={recentSales()}>{(s) => (
                    <div class="v2-tp__sale-row">
                      <span class="v2-tp__sale-item">{s.itemDesc}</span>
                      <span class="v2-tp__sale-gold">{gold(s.price)}</span>
                    </div>
                  )}</For>
                </div>
              </Show>

              {/* ── Fissal's take ── */}
              <div class="v2-section-h">Fissal's take</div>
              <div class="v2-dim" style={{ 'font-size': '11px', 'font-style': 'italic', padding: '4px 0' }}>
                {weekly().advice
                  || '*gears whirr* Jone frequency is quiet — ask Fissal directly for deep trade analysis.'}
              </div>

              {/* ── Editable persona note ── */}
              <div class="v2-section-h" style={{ display: 'flex', 'align-items': 'center', gap: '6px' }}>
                My persona note
                <button class="v2-btn-sm"
                  style={{ 'font-size': '10px', 'margin-left': 'auto' }}
                  onClick={() => { setEditNote(e => !e); }}
                >{editNote() ? 'cancel' : 'edit'}</button>
              </div>

              <Show when={!editNote()}>
                <div style={{
                  'font-size': '11px',
                  'white-space': 'pre-wrap',
                  padding: '4px 0',
                  color: note() ? 'var(--t-green)' : 'var(--t-green-dim)',
                }}>
                  {note() || '(No persona note — add a blurb about your trading style.)'}
                </div>
              </Show>

              <Show when={editNote()}>
                <textarea
                  class="v2-input"
                  rows="4"
                  style={{
                    width: '100%',
                    resize: 'vertical',
                    'box-sizing': 'border-box',
                    'font-size': '11px',
                  }}
                  value={note()}
                  onInput={(e) => setNote(e.currentTarget.value)}
                  placeholder="Trading style, goals, notes…"
                  maxLength={500}
                />
                <div style={{ display: 'flex', gap: '6px', 'margin-top': '4px' }}>
                  <button class="v2-btn-sm" disabled={savingNote()} onClick={saveNote}>
                    {savingNote() ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </Show>

            </div>
          </Show>
        </Show>
      </Show>

    </div>
  );
}
